// Typed fetch wrapper. Every error body is { error: { code, message, issues? } } (domain ApiError).
import type { ApiError as ApiErrorBody, ErrorCode, Issue } from '@upm/domain';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: ErrorCode | 'network',
    message: string,
    public readonly issues: Issue[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
  /** Worth retrying automatically (network drop, 5xx, rate limit). */
  get retryable(): boolean {
    return this.code === 'network' || this.status >= 500 || this.status === 429;
  }
}

export type RequestOptions = {
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

/** Supplies auth headers for /api/admin calls; set by lib/auth. */
type AuthProvider = { headers(): Promise<Record<string, string>>; onUnauthorized(): Promise<boolean> };
let adminAuth: AuthProvider | null = null;
export const setAdminAuth = (p: AuthProvider) => { adminAuth = p; };

async function toError(res: Response): Promise<ApiError> {
  let body: Partial<ApiErrorBody> | null = null;
  try { body = (await res.json()) as ApiErrorBody; } catch { /* not JSON */ }
  const code = (body?.error?.code ?? (res.status === 401 ? 'unauthorized' : res.status === 404 ? 'not_found' : 'internal')) as ErrorCode;
  return new ApiError(res.status, code, body?.error?.message ?? res.statusText ?? 'Request failed', body?.error?.issues ?? []);
}

async function send(method: string, path: string, body: unknown, opts: RequestOptions, retried = false): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json', ...opts.headers };
  const isAdmin = path.startsWith('/api/admin');
  if (isAdmin && adminAuth) Object.assign(headers, await adminAuth.headers());
  let payload: BodyInit | undefined;
  if (body instanceof FormData) payload = body;
  else if (body !== undefined) { headers['Content-Type'] = 'application/json'; payload = JSON.stringify(body); }
  let res: Response;
  try {
    res = await fetch(path, { method, headers, body: payload, credentials: 'same-origin', signal: opts.signal });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError(0, 'network', 'Network error');
  }
  if (res.status === 401 && isAdmin && adminAuth && !retried) {
    if (await adminAuth.onUnauthorized()) return send(method, path, body, opts, true);
  }
  return res;
}

export async function request<T>(method: string, path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
  const res = await send(method, path, body, opts);
  if (!res.ok) throw await toError(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export async function authenticatedBlob(path: string): Promise<Blob> {
  const res = await send('GET', path, undefined, {});
  if (!res.ok) throw await toError(res);
  return res.blob();
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('POST', path, body ?? {}, opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PUT', path, body ?? {}, opts),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PATCH', path, body ?? {}, opts),
  del: <T = void>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('DELETE', path, body, opts),
};

/** Multipart upload with progress (fetch has no upload progress). */
export function uploadWithProgress<T>(
  path: string, form: FormData, onProgress: (fraction: number) => void,
): { promise: Promise<T>; abort: () => void } {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<T>((resolve, reject) => {
    xhr.open('POST', path);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    xhr.onload = () => {
      let body: unknown = null;
      try { body = xhr.responseText ? JSON.parse(xhr.responseText) : null; } catch { /* not JSON */ }
      if (xhr.status >= 200 && xhr.status < 300) { resolve(body as T); return; }
      const err = (body as Partial<ApiErrorBody> | null)?.error;
      reject(new ApiError(xhr.status, (err?.code ?? 'internal') as ErrorCode, err?.message ?? 'Upload failed', err?.issues ?? []));
    };
    xhr.onerror = () => reject(new ApiError(0, 'network', 'Network error'));
    xhr.onabort = () => reject(new ApiError(0, 'network', 'Upload cancelled'));
    xhr.send(form);
  });
  return { promise, abort: () => xhr.abort() };
}
